'use client';

import React from 'react';
import { animate, inView, stagger } from '@motionone/dom';
import {
  clearMotionInline,
  MOTION_DURATION,
  MOTION_EASING,
  prefersReducedMotion,
  setMotionHidden,
} from '@/lib/motion-one';

type MotionStaggerProps = {
  children: React.ReactNode;
  className?: string;
  staggerStep?: number;
  y?: number;
};

export function MotionStagger({
  children,
  className,
  staggerStep = 0.07,
  y = 12,
}: MotionStaggerProps) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const items = Array.from(container.children).filter(
      (node): node is HTMLElement => node instanceof HTMLElement,
    );

    if (prefersReducedMotion()) {
      items.forEach(clearMotionInline);
      return;
    }

    items.forEach((item) => setMotionHidden(item, y));

    const stop = inView(
      container,
      () => {
        animate(
          items,
          { opacity: 1, transform: 'translateY(0)' },
          {
            delay: stagger(staggerStep),
            duration: MOTION_DURATION.fast,
            easing: MOTION_EASING,
          },
        );
      },
      { amount: 'any' },
    );

    return stop;
  }, [staggerStep, y]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
